import React, { useState } from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import styled from '@emotion/styled'
import { isNil } from 'lodash-es'
import { CollateralBasketType, NegotiationActions, NegotiationDetail, NegotiationFieldMeta, RepoType, UserPreference } from 'api'
import { ModalContent, ModalFooter, ModalWindow } from 'view/components/panels'
import { DialogField, NumericInputField } from 'view/components/inputs'
import { TextButton, TextButtonType } from 'view/components/buttons'
import { createFilteredNegotiationDetailsSelector } from 'selectors/negotiations'
import { createUserPreferenceSelector } from 'selectors/userPreferenceSelectors'
import { canUpdateAttributes, getNegotiatedValue } from '../shared/utils'
import { transformHaircutInput, transformRateInput } from 'view/shared/datagrid'
import { ModalConsumerProps, NotificationType, withModal } from 'view/shared/modal'
import { Locale, Translation } from 'locale'

type BulkPriceDialogProps = OwnProps & StateProps & DispatchProps

interface OwnProps extends ModalConsumerProps {
  negotiationIds: string[]

  onClose?: () => unknown
}

interface StateProps {
  negotiationDetails: NegotiationDetail[]
  userPreference: UserPreference
}

interface DispatchProps {
  onUpdateNegotiationsBulk: typeof NegotiationActions.updateValuesBulk
}
const negotiationIdsSelector = (_: {}, props: BulkPriceDialogProps) => props.negotiationIds

const mapStateToProps = createStructuredSelector({
  negotiationDetails: createFilteredNegotiationDetailsSelector(negotiationIdsSelector, () => false),
  userPreference: createUserPreferenceSelector()
})

const mapDispatchToProps = {
  onUpdateNegotiationsBulk: NegotiationActions.updateValuesBulk
}

const Content = styled(ModalContent)`
  flex-direction: row;
`

export const BulkPriceDialogComponent: React.FunctionComponent<BulkPriceDialogProps> = props => {
  const { negotiationDetails, userPreference, onUpdateNegotiationsBulk, onClose, showDialog } = props

  const [rate, setRate] = useState<number | null>(null)
  const [haircut, setHaircut] = useState<number | null>(null)

  const handleCancel = () => {
    onClose?.()
  }

  const handleApply = async () => {
    const updateAttributesList = negotiationDetails
      .filter(
        negotiationDetail =>
          canUpdateAttributes(negotiationDetail) && negotiationDetail.negotiation.repoType !== RepoType.STOCK_BORROWING_LENDING
      )
      .map(negotiationDetail => {
        const isBasketIneligible =
          negotiationDetail.productSector?.basketType &&
          [CollateralBasketType.GC_BESPOKE, CollateralBasketType.GC_PLUS].includes(negotiationDetail.productSector?.basketType)

        return {
          negotiationId: negotiationDetail.negotiation.negotiationId,
          updatedValues: {
            [NegotiationFieldMeta.RATE]: isNil(rate) ? undefined : transformRateInput(rate, negotiationDetail.referenceRate),
            [NegotiationFieldMeta.HAIRCUT]:
              isNil(haircut) || isBasketIneligible
                ? undefined
                : transformHaircutInput(haircut, userPreference.haircutSign, false, negotiationDetail.direction)
          },
          updatedRawValues: {
            [NegotiationFieldMeta.RATE]: isNil(rate) ? undefined : rate,
            [NegotiationFieldMeta.HAIRCUT]: isNil(haircut) || isBasketIneligible ? undefined : haircut
          },
          meta: {
            haircutSign: userPreference.haircutSign
          }
        }
      })

    const hasExistingRate = negotiationDetails.some(negotiationDetail => !isNil(getNegotiatedValue(negotiationDetail.negotiation.rate)))
    const hasExistingHaircut = negotiationDetails.some(
      negotiationDetail => !isNil(getNegotiatedValue(negotiationDetail.negotiation.haircut))
    )

    if ((!isNil(rate) && hasExistingRate) || (!isNil(haircut) && hasExistingHaircut)) {
      const dialogArgs = {
        type: NotificationType.Confirm,
        title: Translation.TC_BULK_PRICE,
        message: Translation.SC_BULK_PRICE_CONFIRMATION,
        buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
        cancelButton: Translation.TC_BUTTON_NO,
        defaultButton: Translation.TC_BUTTON_YES
      }
      const dialogResult = await showDialog(dialogArgs)

      if (dialogResult === Translation.TC_BUTTON_YES) {
        onUpdateNegotiationsBulk(updateAttributesList, false)
      }
    } else {
      onUpdateNegotiationsBulk(updateAttributesList, false)
    }
    onClose?.()
  }

  return (
    <ModalWindow title={Locale.translate(Translation.TC_BULK_PRICE)}>
      <Content>
        <DialogField>
          <NumericInputField
            dataTestId={'BulkPrice-TextInputField-Rate'}
            label={Locale.translate(Translation.TC_RATE)}
            value={rate}
            onUpdate={(value: number | null) => setRate(value)}
          />
        </DialogField>
        <DialogField>
          <NumericInputField
            dataTestId={'BulkPrice-TextInputField-Haircut'}
            label={Locale.translate(Translation.TC_HAIRCUT)}
            value={haircut}
            onUpdate={(value: number | null) => setHaircut(value)}
          />
        </DialogField>
      </Content>
      <ModalFooter>
        <TextButton dataTestId={'BulkPrice-TextButton-Cancel'} type={TextButtonType.Tertiary} onClick={handleCancel}>
          {Locale.translate(Translation.TC_BUTTON_CANCEL)}
        </TextButton>
        <TextButton dataTestId={'BulkPrice-TextButton-Apply'} type={TextButtonType.Primary} onClick={handleApply}>
          {Locale.translate(Translation.TC_BUTTON_APPLY)}
        </TextButton>
      </ModalFooter>
    </ModalWindow>
  )
}

export const BulkPriceDialog = withModal(
  connect<StateProps, DispatchProps, OwnProps>(mapStateToProps, mapDispatchToProps)(BulkPriceDialogComponent)
)
